import { Component, OnInit, OnDestroy, Input, Output, EventEmitter, SimpleChanges, OnChanges } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Location } from '@angular/common';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { RunView, Metadata } from '@memberjunction/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { marked } from 'marked';
import { BCSaaSOrganizationSettingEntity, BCSaaSSettingEntity, BCSaaSSettingCategoryEntity, BCSaaSSettingScopeEntity, BCSaaSSettingAllowedScopeEntity } from '@bluecypress/bcsaas-entities';
import { mjBizAppsCommonOrganizationEntity } from '@mj-biz-apps/common-entities';
import { IzzyMetadataEngine, IzzySettingExtensionEntity } from 'mj_generatedentities';
import { BCSaaSChannelEntity } from '@bluecypress/bcsaas-entities';
import { IAdminPanel, AdminPanelStatus, DEFAULT_ADMIN_PANEL_STATUS } from '../admin-panel.interface';

/**
 * Interface for category tree node
 */
interface CategoryNode {
  category: BCSaaSSettingCategoryEntity;
  children: CategoryNode[];
  expanded: boolean;
}

/**
 * Interface for channel override info
 */
interface ChannelOverride {
  channel: BCSaaSChannelEntity;
  orgSetting: BCSaaSOrganizationSettingEntity;
  value: string;
}

/**
 * Interface for setting display with org override info
 */
interface SettingDisplay {
  setting: BCSaaSSettingEntity;
  orgSetting: BCSaaSOrganizationSettingEntity | null;
  currentValue: string | null;
  isOverridden: boolean;
  isEditing: boolean;
  editValue: string;
  /** Channel overrides for this setting (progressive disclosure) */
  channelOverrides: ChannelOverride[];
  /** Whether channel overrides panel is expanded */
  channelOverridesExpanded: boolean;
  /** Long text/markdown values render clamped; this expands the preview */
  previewExpanded?: boolean;
  /** Which channel is being edited (null = none, or channel ID) */
  editingChannelId: string | null;
  /** Edit value for channel override being edited */
  channelEditValue: string;
}

/**
 * Scope type for settings - Organization or Channel level
 */
type SettingScopeType = 'Organization' | 'Channel';

/**
 * State interface for persisting component state
 */
interface SettingsComponentState {
  selectedOrganizationId?: string;
  selectedCategoryId?: string;
  selectedScope?: SettingScopeType;
  selectedChannelId?: string;
  searchQuery?: string;
}

/**
 * Interface for interaction events emitted by the component
 */
interface SettingsInteraction {
  type: 'selectOrganization' | 'selectCategory' | 'saveSetting' | 'revertSetting';
  organizationId?: string;
  categoryId?: string;
  settingId?: string;
}

/**
 * Interface for query params
 */
interface SettingsQueryParams {
  organizationId?: string;
  categoryId?: string;
}

/**
 * Interface for settings inheritance hierarchy info
 * Used to show contextual help about how settings are resolved
 */
interface SettingsHierarchyInfo {
  hasParentOrg: boolean;
  parentOrgName: string | null;
  hasChildOrgs: boolean;
  childOrgCount: number;
  channelCount: number;
  /** True if org has parent, children, or multiple channels - shows detailed help */
  isAdvancedSetup: boolean;
}

/**
 * Settings Component
 *
 * Manages organization settings with:
 * - Hierarchical category tree navigation
 * - Organization selector for super users
 * - Type-aware value controls (toggle, number input, text, JSON editor)
 * - Visual indicators for overridden vs default values
 * - Inline editing with save/revert actions
 */
@Component({
  standalone: false,
  selector: 'izzy-settings',
  templateUrl: './settings.component.html',
  styleUrls: ['./settings.component.css']
})
export class SettingsComponent implements OnInit, OnDestroy, OnChanges, IAdminPanel {

  // ========== Inputs/Outputs ==========
  @Input() organizationId: string | undefined;
  @Input() initialState: SettingsComponentState | null = null;
  @Output() stateChange = new EventEmitter<SettingsComponentState>();
  @Output() statusChange = new EventEmitter<AdminPanelStatus>();
  @Output() openRecord = new EventEmitter<{ EntityName: string; RecordID: string }>();
  @Output() interaction = new EventEmitter<SettingsInteraction>();

  constructor(
    private location: Location,
    private route: ActivatedRoute,
    private sanitizer: DomSanitizer
  ) {}

  // ========== Data ==========
  protected categories: BCSaaSSettingCategoryEntity[] = [];
  protected categoryTree: CategoryNode[] = [];
  protected settings: BCSaaSSettingEntity[] = [];
  protected organizations: mjBizAppsCommonOrganizationEntity[] = [];
  protected orgSettings: BCSaaSOrganizationSettingEntity[] = [];
  protected channels: BCSaaSChannelEntity[] = [];
  protected settingScopes: BCSaaSSettingScopeEntity[] = [];
  protected settingAllowedScopes: BCSaaSSettingAllowedScopeEntity[] = [];
  protected settingExtensions: IzzySettingExtensionEntity[] = [];

  // ========== Selection State ==========
  protected selectedOrganization: mjBizAppsCommonOrganizationEntity | null = null;
  protected selectedCategory: BCSaaSSettingCategoryEntity | null = null;
  protected settingsDisplay: SettingDisplay[] = [];

  // Note: selectedScope and selectedChannel removed - now using progressive disclosure per-setting

  // ========== Computed: Channels for selected org ==========
  protected get orgChannels(): BCSaaSChannelEntity[] {
    if (!this.selectedOrganization) return [];
    return this.channels.filter(c => c.OrganizationID === this.selectedOrganization!.ID);
  }

  // ========== UI State ==========
  protected isLoading: boolean = true;
  protected isSaving: boolean = false;
  protected errorMessage: string = '';

  // ========== Inline Save Status (subtle indicator) ==========
  protected saveStatusVisible: boolean = false;
  protected saveStatusMessage: string = '';
  protected saveStatusError: boolean = false;
  private saveStatusTimeout: ReturnType<typeof setTimeout> | null = null;

  // ========== Search ==========
  protected searchQuery: string = '';

  // ========== Confirm Dialog ==========
  protected confirmDialogVisible: boolean = false;
  protected confirmDialogTitle: string = '';
  protected confirmDialogMessage: string = '';
  protected confirmDialogAction: (() => Promise<void>) | null = null;

  // ========== Hierarchy Help ==========
  protected hierarchyHelpExpanded: boolean = false;
  protected hierarchyInfo: SettingsHierarchyInfo = {
    hasParentOrg: false,
    parentOrgName: null,
    hasChildOrgs: false,
    childOrgCount: 0,
    channelCount: 0,
    isAdvancedSetup: false
  };

  // ========== Cleanup ==========
  private destroy$ = new Subject<void>();

  async ngOnInit(): Promise<void> {
    // Subscribe to query param changes for deep linking
    this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe(params => {
      this.handleQueryParams(params);
    });

    await this.loadData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // React to organizationId input changes
    if (changes['organizationId'] && !changes['organizationId'].firstChange) {
      const newOrgId = changes['organizationId'].currentValue;
      if (newOrgId && this.organizations.length > 0) {
        const org = this.organizations.find(o => o.ID === newOrgId);
        if (org && org.ID !== this.selectedOrganization?.ID) {
          this.selectOrganization(org);
        }
      }
    }
  }

  /**
   * Handle query params for deep linking
   */
  private handleQueryParams(params: SettingsQueryParams): void {
    // Store params to apply after data loads
    if (params.organizationId) {
      this._pendingOrgId = params.organizationId;
    }
    if (params.categoryId) {
      this._pendingCategoryId = params.categoryId;
    }

    // If data is already loaded, apply immediately
    if (!this.isLoading && this.organizations.length > 0) {
      this.applyPendingSelections();
    }
  }

  private _pendingOrgId: string | null = null;
  private _pendingCategoryId: string | null = null;

  /**
   * Apply pending selections from query params
   */
  private applyPendingSelections(): void {
    if (this._pendingOrgId) {
      const org = this.organizations.find(o => o.ID === this._pendingOrgId);
      if (org && org.ID !== this.selectedOrganization?.ID) {
        this.selectOrganization(org);
      }
      this._pendingOrgId = null;
    }

    if (this._pendingCategoryId) {
      const cat = this.categories.find(c => c.ID === this._pendingCategoryId);
      if (cat && cat.ID !== this.selectedCategory?.ID) {
        this.selectCategory(cat);
      }
      this._pendingCategoryId = null;
    }
  }

  /**
   * Update URL query params without changing route path
   */
  private updateQueryParams(): void {
    const params = new URLSearchParams();

    if (this.selectedOrganization) {
      params.set('organizationId', this.selectedOrganization.ID);
    }
    if (this.selectedCategory) {
      params.set('categoryId', this.selectedCategory.ID);
    }

    // Get current path and update only query string
    const path = this.location.path().split('?')[0];
    const queryString = params.toString();
    const newUrl = queryString ? `${path}?${queryString}` : path;
    this.location.replaceState(newUrl);
  }

  protected async loadData(): Promise<void> {
    this.isLoading = true;
    this.errorMessage = '';

    try {
      // Initialize IzzyMetadataEngine (does nothing if already initialized)
      await IzzyMetadataEngine.Instance.Config(false);

      // Use cached data from IzzyMetadataEngine for static reference data
      this.organizations = IzzyMetadataEngine.Instance.GetCurrentUserOrganizations();
      this.categories = IzzyMetadataEngine.Instance.SettingCategories;
      this.settings = IzzyMetadataEngine.Instance.Settings;
      this.channels = IzzyMetadataEngine.Instance.Channels;

      // Load setting scopes, allowed scopes, and extensions (not cached in IzzyMetadataEngine)
      const rv = new RunView();
      const [scopesResult, allowedScopesResult, extensionsResult] = await Promise.all([
        rv.RunView<BCSaaSSettingScopeEntity>({ EntityName: 'BC: Setting Scopes', ResultType: 'entity_object' }),
        rv.RunView<BCSaaSSettingAllowedScopeEntity>({ EntityName: 'BC: Setting Allowed Scopes', ResultType: 'entity_object' }),
        rv.RunView<IzzySettingExtensionEntity>({ EntityName: 'Setting Extensions', ResultType: 'entity_object' })
      ]);
      this.settingScopes = scopesResult?.Success ? scopesResult.Results : [];
      this.settingAllowedScopes = allowedScopesResult?.Success ? allowedScopesResult.Results : [];
      this.settingExtensions = extensionsResult?.Success ? extensionsResult.Results : [];

      // Build category tree
      this.buildCategoryTree();

      // Apply pending selections from query params, or use organizationId input
      if (this._pendingOrgId || this._pendingCategoryId) {
        this.applyPendingSelections();
      } else if (this.organizationId) {
        // Use the organizationId input from parent
        const org = this.organizations.find(o => o.ID === this.organizationId);
        if (org) {
          await this.selectOrganization(org);
        }
      } else if (this.organizations.length > 0 && !this.selectedOrganization) {
        // Fallback to first org if no input provided
        await this.selectOrganization(this.organizations[0]);
      }

      // Auto-select first root category if available
      if (this.categoryTree.length > 0 && !this.selectedCategory) {
        this.selectCategory(this.categoryTree[0].category);
      }

      // Emit default status (settings don't have a health indicator)
      this.statusChange.emit(DEFAULT_ADMIN_PANEL_STATUS);
    } catch (error) {
      console.error('Error loading settings data:', error);
      this.errorMessage = 'Failed to load settings. Please try again.';
    } finally {
      this.isLoading = false;
    }
  }

  private buildCategoryTree(): void {
    const nodeMap = new Map<string, CategoryNode>();

    // Create nodes for all categories
    for (const category of this.categories) {
      nodeMap.set(category.ID, {
        category,
        children: [],
        expanded: true
      });
    }

    // Build tree structure
    this.categoryTree = [];
    for (const category of this.categories) {
      const node = nodeMap.get(category.ID)!;
      if (category.ParentID) {
        const parentNode = nodeMap.get(category.ParentID);
        if (parentNode) {
          parentNode.children.push(node);
        } else {
          this.categoryTree.push(node);
        }
      } else {
        this.categoryTree.push(node);
      }
    }

    // Sort children by Sequence, then by Name as tiebreaker
    const sortNodes = (nodes: CategoryNode[]) => {
      nodes.sort((a, b) => {
        const seqDiff = (a.category.Sequence || 0) - (b.category.Sequence || 0);
        return seqDiff !== 0 ? seqDiff : a.category.Name.localeCompare(b.category.Name);
      });
      nodes.forEach(n => sortNodes(n.children));
    };
    sortNodes(this.categoryTree);
  }

  // ========== Organization Selection ==========

  /**
   * Handler for organization dropdown change event
   */
  onOrganizationChange(orgId: string): void {
    const org = this.organizations.find(o => o.ID === orgId);
    if (org) {
      this.selectOrganization(org);
    }
  }

  async selectOrganization(org: mjBizAppsCommonOrganizationEntity): Promise<void> {
    this.selectedOrganization = org;
    await this.loadOrgSettings();
    this.updateSettingsDisplay();
    this.updateHierarchyInfo();
    this.updateQueryParams();
  }

  /**
   * Updates the hierarchy info for the selected organization
   * Used to show contextual help about settings inheritance
   */
  private updateHierarchyInfo(): void {
    if (!this.selectedOrganization) {
      this.hierarchyInfo = {
        hasParentOrg: false,
        parentOrgName: null,
        hasChildOrgs: false,
        childOrgCount: 0,
        channelCount: 0,
        isAdvancedSetup: false
      };
      return;
    }

    // Check for parent org
    const hasParentOrg = !!this.selectedOrganization.ParentID;
    let parentOrgName: string | null = null;
    if (hasParentOrg) {
      const parentOrg = this.organizations.find(o => o.ID === this.selectedOrganization!.ParentID);
      parentOrgName = parentOrg?.Name || null;
    }

    // Check for child orgs
    const childOrgs = this.organizations.filter(o => o.ParentID === this.selectedOrganization!.ID);
    const hasChildOrgs = childOrgs.length > 0;
    const childOrgCount = childOrgs.length;

    // Count channels for this org
    const channels = IzzyMetadataEngine.Instance.Channels.filter(
      c => c.OrganizationID === this.selectedOrganization!.ID
    );
    const channelCount = channels.length;

    // Determine if this is an "advanced" setup that warrants showing detailed help
    const isAdvancedSetup = hasParentOrg || hasChildOrgs || channelCount > 1;

    this.hierarchyInfo = {
      hasParentOrg,
      parentOrgName,
      hasChildOrgs,
      childOrgCount,
      channelCount,
      isAdvancedSetup
    };
  }

  /**
   * Toggle the hierarchy help panel visibility
   */
  toggleHierarchyHelp(): void {
    this.hierarchyHelpExpanded = !this.hierarchyHelpExpanded;
  }

  /**
   * Check if the current category is Auto Approve (or a parent of it)
   * Used to show contextual help about settings inheritance
   */
  get isAutoApproveCategory(): boolean {
    if (!this.selectedCategory) return false;
    return this.selectedCategory.Name === 'Auto Approve' ||
           this.selectedCategory.Name.toLowerCase().includes('auto approve');
  }

  /**
   * Check if the current category is Website Sync
   */
  get isWebsiteSyncCategory(): boolean {
    return this.selectedCategory?.Name === 'Website Sync';
  }

  // ========== Website URL Editing ==========

  protected websiteUrlEditValue: string = '';
  protected isEditingWebsiteUrl: boolean = false;
  protected isSavingWebsiteUrl: boolean = false;

  startEditingWebsiteUrl(): void {
    this.websiteUrlEditValue = this.selectedOrganization?.Website || '';
    this.isEditingWebsiteUrl = true;
  }

  cancelEditingWebsiteUrl(): void {
    this.isEditingWebsiteUrl = false;
  }

  async saveWebsiteUrl(): Promise<void> {
    if (!this.selectedOrganization) return;
    this.isSavingWebsiteUrl = true;
    try {
      this.selectedOrganization.Website = this.websiteUrlEditValue;
      const saved = await this.selectedOrganization.Save();
      if (!saved) {
        console.error('Error saving website URL:', this.selectedOrganization.LatestResult?.CompleteMessage ?? 'unknown error');
        return;
      }
      this.isEditingWebsiteUrl = false;
    } catch (error) {
      console.error('Error saving website URL:', error);
    } finally {
      this.isSavingWebsiteUrl = false;
    }
  }

  /**
   * Check if we should show the hierarchy help hint
   * Shows only for advanced setups on relevant categories
   */
  get showHierarchyHelpHint(): boolean {
    return this.hierarchyInfo.isAdvancedSetup && this.isAutoApproveCategory;
  }

  /**
   * Load org settings using RunView (not cached) since these change frequently
   */
  private async loadOrgSettings(): Promise<void> {
    if (!this.selectedOrganization) {
      this.orgSettings = [];
      return;
    }

    try {
      const rv = new RunView();
      const result = await rv.RunView<BCSaaSOrganizationSettingEntity>({
        EntityName: 'BC: Organization Settings',
        ExtraFilter: `OrganizationID = '${this.selectedOrganization.ID}'`,
        ResultType: 'entity_object'
      });
      this.orgSettings = result?.Success ? result.Results : [];
    } catch (error) {
      console.error('Error loading org settings:', error);
      this.orgSettings = [];
    }
  }

  // ========== Category Selection ==========

  selectCategory(category: BCSaaSSettingCategoryEntity): void {
    this.selectedCategory = category;
    this.updateSettingsDisplay();
    this.updateQueryParams();
  }

  toggleCategoryExpand(node: CategoryNode, event: Event): void {
    event.stopPropagation();
    node.expanded = !node.expanded;
  }

  isCategorySelected(category: BCSaaSSettingCategoryEntity): boolean {
    return this.selectedCategory?.ID === category.ID;
  }

  /**
   * Check if this node is the parent of the currently selected category
   * Used to show sub-tabs when a parent category is active
   */
  isParentOfSelected(node: CategoryNode): boolean {
    if (!this.selectedCategory) return false;

    // Check if the selected category is a direct child
    const isDirectParent = node.children.some(
      child => child.category.ID === this.selectedCategory?.ID
    );
    if (isDirectParent) return true;

    // Check if the selected category IS this node (show children when parent is selected)
    if (node.category.ID === this.selectedCategory.ID && node.children.length > 0) {
      return true;
    }

    return false;
  }

  // ========== Settings Display ==========

  private updateSettingsDisplay(): void {
    if (!this.selectedCategory) {
      this.settingsDisplay = [];
      return;
    }

    const orgScope = this.settingScopes.find(s => s.Name === 'Organization');
    const channelScope = this.settingScopes.find(s => s.Name === 'Channel');

    // Get all descendant category IDs
    const categoryIds = this.getDescendantCategoryIds(this.selectedCategory.ID);
    categoryIds.add(this.selectedCategory.ID);

    // Filter settings by category
    const categorySettings = this.settings.filter(s => {
      if (!categoryIds.has(s.CategoryID)) return false;
      // For now, show all settings but use AllowedScopes to control UI behavior
      return true;
    });

    // Build display objects - always show org-level value as the primary
    this.settingsDisplay = categorySettings.map(setting => {
      // Get organization-scoped setting (primary)
      const orgSetting = (orgScope ? this.orgSettings.find(os =>
        os.SettingID === setting.ID &&
        os.ScopeID === orgScope.ID
      ) : null) || null;

      // Get all channel overrides for this setting
      const channelOverrides: ChannelOverride[] = [];
      if (this.allowsChannelScope(setting) && channelScope) {
        for (const channel of this.orgChannels) {
          const channelOrgSetting = this.orgSettings.find(os =>
            os.SettingID === setting.ID &&
            os.ScopeID === channelScope.ID &&
            os.ScopeEntityID === channel.ID
          );
          if (channelOrgSetting) {
            channelOverrides.push({
              channel,
              orgSetting: channelOrgSetting,
              value: channelOrgSetting.Value || ''
            });
          }
        }
      }

      const currentValue = orgSetting?.Value ?? setting.DefaultValue;

      return {
        setting,
        orgSetting,
        currentValue,
        isOverridden: orgSetting !== null,
        isEditing: false,
        editValue: currentValue || '',
        channelOverrides,
        channelOverridesExpanded: false,
        editingChannelId: null,
        channelEditValue: ''
      };
    });

    // Sort by Sequence, then by Name as tiebreaker
    this.settingsDisplay.sort((a, b) => {
      const seqDiff = (a.setting.Sequence || 0) - (b.setting.Sequence || 0);
      return seqDiff !== 0 ? seqDiff : a.setting.Name.localeCompare(b.setting.Name);
    });
  }

  private getDescendantCategoryIds(parentId: string): Set<string> {
    const ids = new Set<string>();
    const addDescendants = (pId: string) => {
      for (const cat of this.categories) {
        if (cat.ParentID === pId) {
          ids.add(cat.ID);
          addDescendants(cat.ID);
        }
      }
    };
    addDescendants(parentId);
    return ids;
  }

  get filteredSettings(): SettingDisplay[] {
    if (!this.searchQuery.trim()) {
      return this.settingsDisplay;
    }
    const query = this.searchQuery.toLowerCase();
    return this.settingsDisplay.filter(sd =>
      sd.setting.Name.toLowerCase().includes(query) ||
      (sd.setting.Description?.toLowerCase().includes(query))
    );
  }

  // ========== Category Controller Logic ==========

  /**
   * Check if a setting is the category controller (master toggle)
   */
  isCategoryController(sd: SettingDisplay): boolean {
    const ext = this.settingExtensions.find(se => se.SettingID === sd.setting.ID);
    return ext?.IsCategoryController === true;
  }

  /**
   * Check if this setting should be disabled because the category controller is off.
   * A setting is disabled if:
   * 1. It's in the same category as a category controller setting
   * 2. It's NOT the category controller itself
   * 3. The category controller value is 'false' or falsy
   */
  isDisabledByController(sd: SettingDisplay): boolean {
    const ext = this.settingExtensions.find(se => se.SettingID === sd.setting.ID);
    if (ext?.IsCategoryController) {
      return false;
    }

    const controller = this.settingsDisplay.find(s => {
      const sExt = this.settingExtensions.find(se => se.SettingID === s.setting.ID);
      return s.setting.CategoryID === sd.setting.CategoryID && sExt?.IsCategoryController;
    });

    if (!controller) {
      return false;
    }

    const controllerValue = controller.currentValue?.toLowerCase();
    return controllerValue !== 'true';
  }

  // ========== Plan-Level Gating ==========

  /**
   * The selected organization's effective plan level (0 if no active plan/subscription).
   * Resolved via the org's Active/Trialing Subscription → PlanPrice → Plan.Level.
   */
  private get organizationPlanLevel(): number {
    if (!this.selectedOrganization) return 0;
    const plan = IzzyMetadataEngine.Instance.GetOrganizationPlan(this.selectedOrganization.ID);
    return plan?.Level ?? 0;
  }

  /**
   * True when this setting requires a higher plan than the organization currently has.
   * Such settings are shown but their toggle/editor is disabled (cannot be turned on),
   * with an explanatory message (see getPlanGateMessage).
   */
  isDisabledByPlan(sd: SettingDisplay): boolean {
    const required = sd.setting.MinimumPlanLevel ?? 0;
    if (required <= 0) return false;
    return this.organizationPlanLevel < required;
  }

  /**
   * Explanatory message shown when a setting is gated by plan level. Names the plan the
   * org needs to upgrade to. Returns '' when the setting is not plan-gated for this org.
   */
  getPlanGateMessage(sd: SettingDisplay): string {
    if (!this.isDisabledByPlan(sd)) return '';
    const required = sd.setting.MinimumPlanLevel ?? 0;
    const requiredPlan = IzzyMetadataEngine.Instance.GetPlanByLevel(required);
    const planName = requiredPlan?.Name || `plan level ${required}`;
    return `Requires the ${planName} plan. Upgrade to enable this setting.`;
  }

  /**
   * Combined disable check used by the template: a setting is disabled if its category
   * controller is off OR the organization's plan is too low.
   */
  isSettingDisabled(sd: SettingDisplay): boolean {
    return this.isDisabledByController(sd) || this.isDisabledByPlan(sd);
  }

  // ========== Value Editing ==========

  startEditing(sd: SettingDisplay): void {
    sd.isEditing = true;
    sd.editValue = sd.currentValue || '';
  }

  cancelEditing(sd: SettingDisplay): void {
    sd.isEditing = false;
    sd.editValue = sd.currentValue || '';
  }

  async saveSettingValue(sd: SettingDisplay): Promise<void> {
    if (!this.selectedOrganization) return;

    this.isSaving = true;
    try {
      const md = new Metadata();

      const valueAsString = String(sd.editValue);
      if (sd.orgSetting) {
        // Update existing org setting
        sd.orgSetting.Value = valueAsString;
        await sd.orgSetting.Save();
      } else {
        // Create new org setting (always at Organization scope for main value)
        const orgScope = this.settingScopes.find(s => s.Name === 'Organization');
        if (!orgScope) throw new Error('Organization scope not found');
        const newOrgSetting = await md.GetEntityObject<BCSaaSOrganizationSettingEntity>('BC: Organization Settings');
        newOrgSetting.OrganizationID = this.selectedOrganization.ID;
        newOrgSetting.SettingID = sd.setting.ID;
        newOrgSetting.Value = valueAsString;
        newOrgSetting.ScopeID = orgScope.ID;

        await newOrgSetting.Save();
        sd.orgSetting = newOrgSetting;

        // Reload to get the saved entity
        await this.loadOrgSettings();
        this.updateSettingsDisplay();
      }

      sd.currentValue = valueAsString;
      sd.isEditing = false;
      sd.isOverridden = true;

      this.showSaveStatus('Saved');
    } catch (error) {
      console.error('Error saving setting:', error);
      this.errorMessage = 'Failed to save setting. Please try again.';
    } finally {
      this.isSaving = false;
    }
  }

  revertToDefault(sd: SettingDisplay): void {
    if (!sd.orgSetting) return;

    this.showConfirmDialog(
      'Revert to Default',
      `Are you sure you want to revert "${sd.setting.Name}" to its default value?`,
      async () => {
        this.isSaving = true;
        try {
          await sd.orgSetting!.Delete();

          // Reload org settings
          await this.loadOrgSettings();
          this.updateSettingsDisplay();

          this.showSaveStatus('Reverted');
        } catch (error) {
          console.error('Error reverting setting:', error);
          this.errorMessage = 'Failed to revert setting. Please try again.';
        } finally {
          this.isSaving = false;
        }
      }
    );
  }

  // ========== Confirm Dialog Methods ==========

  protected showConfirmDialog(title: string, message: string, action: () => Promise<void>): void {
    this.confirmDialogTitle = title;
    this.confirmDialogMessage = message;
    this.confirmDialogAction = action;
    this.confirmDialogVisible = true;
  }

  protected async confirmDialogYes(): Promise<void> {
    if (this.confirmDialogAction) {
      await this.confirmDialogAction();
    }
    this.closeConfirmDialog();
  }

  protected closeConfirmDialog(): void {
    this.confirmDialogVisible = false;
    this.confirmDialogAction = null;
  }

  // ========== Boolean Toggle ==========

  async toggleBooleanSetting(sd: SettingDisplay): Promise<void> {
    // Defense in depth: never allow toggling a setting that is disabled by its category
    // controller or gated above the org's plan level (the input is also [disabled] in the UI).
    if (this.isSettingDisabled(sd)) {
      return;
    }
    const currentBool = sd.currentValue?.toLowerCase() === 'true';
    sd.editValue = (!currentBool).toString();
    await this.saveSettingValue(sd);
  }

  // ========== Utilities ==========

  getValueTypeIcon(valueType: string): string {
    switch (valueType) {
      case 'Boolean': return 'fa-toggle-on';
      case 'Number': return 'fa-hashtag';
      case 'JSON': return 'fa-code';
      case 'Date': return 'fa-calendar';
      case 'Markdown': return 'fa-file-lines';
      case 'HTML': return 'fa-code';
      default: return 'fa-font';
    }
  }

  getEffectiveValueType(setting: BCSaaSSettingEntity): string {
    const ext = this.settingExtensions.find(se => se.SettingID === setting.ID);
    return ext?.ValueType || setting.ValueType;
  }

  getSettingIcon(setting: BCSaaSSettingEntity): string {
    const ext = this.settingExtensions.find(se => se.SettingID === setting.ID);
    if (ext?.Icon) return ext.Icon;
    return this.getValueTypeIcon(this.getEffectiveValueType(setting));
  }

  isBoolean(sd: SettingDisplay): boolean {
    return this.getEffectiveValueType(sd.setting) === 'Boolean';
  }

  getBooleanValue(sd: SettingDisplay): boolean {
    return sd.currentValue?.toLowerCase() === 'true';
  }

  isNumber(sd: SettingDisplay): boolean {
    return this.getEffectiveValueType(sd.setting) === 'Number';
  }

  isJSON(sd: SettingDisplay): boolean {
    return this.getEffectiveValueType(sd.setting) === 'JSON';
  }

  isMarkdown(sd: SettingDisplay): boolean {
    return this.getEffectiveValueType(sd.setting) === 'Markdown';
  }

  isHTML(sd: SettingDisplay): boolean {
    return this.getEffectiveValueType(sd.setting) === 'HTML';
  }

  /**
   * Check if this is a "rich" type that needs a special editor (JSON, Markdown, HTML)
   */
  isRichType(sd: SettingDisplay): boolean {
    return this.isJSON(sd) || this.isMarkdown(sd) || this.isHTML(sd);
  }

  /**
   * A List-type setting renders as a dropdown of fixed options (e.g. an enum). The effective
   * type 'List' comes from the SettingExtension; the options come from its AllowedValues JSON.
   */
  isList(sd: SettingDisplay): boolean {
    return this.getEffectiveValueType(sd.setting) === 'List';
  }

  /**
   * The allowed dropdown options for a List setting, parsed from the SettingExtension's
   * AllowedValues JSON array (e.g. ["None","RoundRobin","LeastLoad"]). Empty when not set or invalid.
   */
  getListOptions(sd: SettingDisplay): string[] {
    const ext = this.settingExtensions.find(se => se.SettingID === sd.setting.ID);
    if (!ext?.AllowedValues) return [];
    try {
      const parsed = JSON.parse(ext.AllowedValues);
      return Array.isArray(parsed) ? parsed.map(v => String(v)) : [];
    } catch {
      return [];
    }
  }

  /**
   * Human-friendly display label for a List/MultiList option. The stored value (e.g. 'RoundRobin',
   * 'LeastLoad', 'skill_based') is what the backend matches on, so it is never mutated — this is
   * display-only. Splits camelCase/PascalCase boundaries and underscores/hyphens into spaced words
   * ('RoundRobin' → 'Round Robin'), Title-cases each, but preserves all-caps acronym tokens
   * ('SLA' stays 'SLA', not 'Sla').
   */
  humanizeOption(value: string): string {
    if (!value) return value;
    return value
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2') // camelCase boundary
      .replace(/[_-]+/g, ' ')                  // underscores / hyphens → space
      .trim()
      .split(/\s+/)
      .map(w => (w.length > 1 && w === w.toUpperCase()) ? w : w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
  }

  /**
   * Sets a List setting to the chosen option and saves immediately (mirrors the boolean toggle —
   * no separate edit mode for a single-select dropdown).
   */
  async setListValue(sd: SettingDisplay, value: string): Promise<void> {
    if (this.isSettingDisabled(sd)) {
      return;
    }
    sd.editValue = value;
    await this.saveSettingValue(sd);
  }

  /**
   * A MultiList setting is a multi-select of fixed options (effective type 'MultiList'); options
   * come from AllowedValues (same as List) and the value is stored as a JSON array of the selected
   * options.
   */
  isMultiList(sd: SettingDisplay): boolean {
    return this.getEffectiveValueType(sd.setting) === 'MultiList';
  }

  /** The currently-selected values of a MultiList setting, parsed from its JSON-array value. */
  getMultiListValues(sd: SettingDisplay): string[] {
    if (!sd.currentValue) return [];
    try {
      const parsed = JSON.parse(sd.currentValue);
      return Array.isArray(parsed) ? parsed.map(v => String(v)) : [];
    } catch {
      // Tolerate a legacy comma-separated value (pre-MultiList) so nothing is lost on first edit.
      return sd.currentValue.split(',').map(v => v.trim()).filter(v => v.length > 0);
    }
  }

  isMultiListSelected(sd: SettingDisplay, option: string): boolean {
    return this.getMultiListValues(sd).includes(option);
  }

  /**
   * Toggles one option of a MultiList setting on/off and saves immediately. The value is persisted
   * as a JSON array of the selected options.
   */
  async toggleMultiListValue(sd: SettingDisplay, option: string): Promise<void> {
    if (this.isSettingDisabled(sd)) {
      return;
    }
    const selected = new Set(this.getMultiListValues(sd));
    if (selected.has(option)) {
      selected.delete(option);
    } else {
      selected.add(option);
    }
    // Preserve AllowedValues order for a stable, readable stored value.
    const ordered = this.getListOptions(sd).filter(o => selected.has(o));
    sd.editValue = JSON.stringify(ordered);
    await this.saveSettingValue(sd);
  }

  // ========== AllowedScopes Helpers ==========

  /**
   * Check if a setting allows organization-level scope
   */
  allowsOrgScope(setting: BCSaaSSettingEntity): boolean {
    const allowedScopeNames = this.getSettingAllowedScopeNames(setting.ID);
    return allowedScopeNames.length === 0 || allowedScopeNames.includes('Organization');
  }

  /**
   * Check if a setting allows channel-level scope
   */
  allowsChannelScope(setting: BCSaaSSettingEntity): boolean {
    const allowedScopeNames = this.getSettingAllowedScopeNames(setting.ID);
    return allowedScopeNames.length === 0 || allowedScopeNames.includes('Channel');
  }

  private getSettingAllowedScopeNames(settingId: string): string[] {
    return this.settingAllowedScopes
      .filter(sas => sas.SettingID === settingId)
      .map(sas => {
        const scope = this.settingScopes.find(s => s.ID === sas.ScopeID);
        return scope?.Name || '';
      })
      .filter(name => name.length > 0);
  }

  /**
   * Check if a setting is organization-only (no channel overrides allowed)
   */
  isOrgOnlySetting(sd: SettingDisplay): boolean {
    return this.allowsOrgScope(sd.setting) && !this.allowsChannelScope(sd.setting);
  }

  /**
   * Check if a setting is channel-only (no org-level setting)
   */
  isChannelOnlySetting(sd: SettingDisplay): boolean {
    return !this.allowsOrgScope(sd.setting) && this.allowsChannelScope(sd.setting);
  }

  /**
   * Check if this setting supports channel overrides (both org and channel scopes)
   * Only show channel override UI if org has 2+ channels - with just one channel,
   * overriding org-level doesn't make sense since that channel IS the org effectively
   */
  showChannelOverrideUI(sd: SettingDisplay): boolean {
    if (!this.allowsChannelScope(sd.setting)) return false;
    // Only show if org has 2+ channels - with just one channel, no point in overrides
    return this.orgChannels.length >= 2;
  }

  /**
   * Toggle channel overrides panel visibility
   */
  toggleChannelOverrides(sd: SettingDisplay): void {
    sd.channelOverridesExpanded = !sd.channelOverridesExpanded;
  }

  /**
   * Get channels that don't have overrides for this setting
   */
  getChannelsWithoutOverride(sd: SettingDisplay): BCSaaSChannelEntity[] {
    const overriddenChannelIds = new Set(sd.channelOverrides.map(co => co.channel.ID));
    return this.orgChannels.filter(c => !overriddenChannelIds.has(c.ID));
  }

  /**
   * Start editing a channel override
   */
  startChannelEdit(sd: SettingDisplay, channelId: string): void {
    const override = sd.channelOverrides.find(co => co.channel.ID === channelId);
    sd.editingChannelId = channelId;
    sd.channelEditValue = override?.value || sd.currentValue || '';
  }

  /**
   * Cancel editing a channel override
   */
  cancelChannelEdit(sd: SettingDisplay): void {
    sd.editingChannelId = null;
    sd.channelEditValue = '';
  }

  /**
   * Save a channel override
   */
  async saveChannelOverride(sd: SettingDisplay, channelId: string): Promise<void> {
    if (!this.selectedOrganization) return;

    this.isSaving = true;
    try {
      const md = new Metadata();
      const channel = this.orgChannels.find(c => c.ID === channelId);
      if (!channel) throw new Error('Channel not found');

      // Check if override already exists
      const existingOverride = sd.channelOverrides.find(co => co.channel.ID === channelId);
      const channelValueAsString = String(sd.channelEditValue);

      if (existingOverride) {
        // Update existing
        existingOverride.orgSetting.Value = channelValueAsString;
        await existingOverride.orgSetting.Save();
        existingOverride.value = channelValueAsString;
      } else {
        // Create new channel override
        const channelScope = this.settingScopes.find(s => s.Name === 'Channel');
        if (!channelScope) throw new Error('Channel scope not found');
        const newOrgSetting = await md.GetEntityObject<BCSaaSOrganizationSettingEntity>('BC: Organization Settings');
        newOrgSetting.OrganizationID = this.selectedOrganization.ID;
        newOrgSetting.SettingID = sd.setting.ID;
        newOrgSetting.Value = channelValueAsString;
        newOrgSetting.ScopeID = channelScope.ID;
        newOrgSetting.ScopeEntityID = channelId;
        await newOrgSetting.Save();

        // Add to local list
        sd.channelOverrides.push({
          channel,
          orgSetting: newOrgSetting,
          value: channelValueAsString
        });

        // Reload to get complete data
        await this.loadOrgSettings();
      }

      sd.editingChannelId = null;
      sd.channelEditValue = '';
      this.showSaveStatus('Channel override saved');
    } catch (error) {
      console.error('Error saving channel override:', error);
      this.errorMessage = 'Failed to save channel override. Please try again.';
    } finally {
      this.isSaving = false;
    }
  }

  /**
   * Remove a channel override (revert to org-level value)
   */
  async removeChannelOverride(sd: SettingDisplay, channelId: string): Promise<void> {
    const override = sd.channelOverrides.find(co => co.channel.ID === channelId);
    if (!override) return;

    this.showConfirmDialog(
      'Remove Channel Override',
      `Remove override for "${override.channel.Name}"? This channel will use the organization default.`,
      async () => {
        this.isSaving = true;
        try {
          await override.orgSetting.Delete();

          // Remove from local list
          const idx = sd.channelOverrides.findIndex(co => co.channel.ID === channelId);
          if (idx >= 0) {
            sd.channelOverrides.splice(idx, 1);
          }

          // Reload to sync
          await this.loadOrgSettings();
          this.showSaveStatus('Override removed');
        } catch (error) {
          console.error('Error removing channel override:', error);
          this.errorMessage = 'Failed to remove override. Please try again.';
        } finally {
          this.isSaving = false;
        }
      }
    );
  }

  /**
   * Toggle a boolean value for a channel override
   */
  async toggleChannelBooleanOverride(sd: SettingDisplay, channelId: string): Promise<void> {
    const override = sd.channelOverrides.find(co => co.channel.ID === channelId);
    const currentBool = override ? override.value.toLowerCase() === 'true' : (sd.currentValue?.toLowerCase() === 'true');
    sd.channelEditValue = (!currentBool).toString();
    await this.saveChannelOverride(sd, channelId);
  }

  /**
   * Check if editing a new channel (not existing override)
   */
  isEditingNewChannel(sd: SettingDisplay): boolean {
    if (!sd.editingChannelId) return false;
    return !sd.channelOverrides.some(co => co.channel.ID === sd.editingChannelId);
  }

  /**
   * Get the name of the channel being edited
   */
  getEditingChannelName(sd: SettingDisplay): string {
    if (!sd.editingChannelId) return '';
    const channel = this.orgChannels.find(c => c.ID === sd.editingChannelId);
    return channel?.Name || '';
  }

  /**
   * Get the value for a specific channel (for channel-only settings list display)
   * Returns the value string if set, or null if not set
   */
  getChannelOverrideValue(sd: SettingDisplay, channelId: string): string | null {
    const override = sd.channelOverrides.find(co => co.channel.ID === channelId);
    return override?.value ?? null;
  }

  /**
   * Clear a channel value (for channel-only settings)
   * This deletes the OrganizationSetting record for that channel
   */
  async clearChannelValue(sd: SettingDisplay, channelId: string): Promise<void> {
    const override = sd.channelOverrides.find(co => co.channel.ID === channelId);
    if (!override) return;

    const channel = this.orgChannels.find(c => c.ID === channelId);
    const channelName = channel?.Name || 'this channel';

    this.showConfirmDialog(
      'Clear Channel Value',
      `Clear the value for "${channelName}"?`,
      async () => {
        this.isSaving = true;
        try {
          await override.orgSetting.Delete();

          // Remove from local list
          const idx = sd.channelOverrides.findIndex(co => co.channel.ID === channelId);
          if (idx >= 0) {
            sd.channelOverrides.splice(idx, 1);
          }

          // Reload to sync
          await this.loadOrgSettings();
          this.showSaveStatus('Value cleared');
        } catch (error) {
          console.error('Error clearing channel value:', error);
          this.errorMessage = 'Failed to clear value. Please try again.';
        } finally {
          this.isSaving = false;
        }
      }
    );
  }

  getCategoryPath(category: BCSaaSSettingCategoryEntity): string {
    const path: string[] = [category.Name];
    let current: BCSaaSSettingCategoryEntity | undefined = category;

    while (current?.ParentID) {
      current = this.categories.find(c => c.ID === current!.ParentID);
      if (current) {
        path.unshift(current.Name);
      }
    }

    return path.join(' / ');
  }

  /**
   * Render markdown content as sanitized HTML
   */
  renderMarkdown(content: string | null | undefined): SafeHtml {
    if (!content) return '';
    const html = marked.parse(content, { async: false }) as string;
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  /**
   * Show a brief inline status indicator (replaces intrusive toast)
   * @param message Short status message
   * @param isError Whether this is an error status
   */
  private showSaveStatus(message: string, isError: boolean = false): void {
    // Clear any existing timeout
    if (this.saveStatusTimeout) {
      clearTimeout(this.saveStatusTimeout);
    }

    this.saveStatusMessage = message;
    this.saveStatusError = isError;
    this.saveStatusVisible = true;

    // Auto-hide after 2 seconds (shorter than old toast)
    this.saveStatusTimeout = setTimeout(() => {
      this.saveStatusVisible = false;
    }, 2000);
  }
}

/**
 * Utility function to prevent tree-shaking
 */
export function LoadSettingsComponent(): void {
  // Intentionally empty - prevents tree-shaking
}
